import type { FC } from 'react'
import { useEffect, useMemo } from 'react'

import { InfoBox, Skeleton } from '@skytech/ui'
import { useQuery } from '@tanstack/react-query'
import orderBy from 'lodash/orderBy'

import { Translation } from '@/app/translations/Translation'
import { NoResults } from '@/components/NoResults'
import { useIsAdmin } from '@/hooks/access'
import { useRenderLimit } from '@/hooks/use-render-limit'
import { useEverSelectedCategories } from '@/modules/ecom-policy/hooks'
import queries from '@/modules/ecom-policy/queries'
import { ecomPolicyResources } from '@/modules/ecom-policy/resources'
import {
  getCostObjectCustomerId,
  getGroupedDataList,
} from '@/modules/ecom-policy/shop/new-order/helpers'
import { useShopNewOrderContext } from '@/modules/ecom-policy/shop/new-order/ShopNewOrder.context'
import employeeResources from '@/resources/employees'
import { commonQueries } from '@/resources/queries'

import Filter from '../Filter'
import ProductGroup from '../ProductGroup'
import ProgressButtons from '../ProgressButtons'

import '../products.scss'

const StepProducts: FC<{
  orderSummary:
    | {
        cost: number
        products: number
        currency: string | null
      }
    | undefined
  isAccessoriesAvailable: boolean
  advanceWizard?: () => void
  regressWizard?: () => void
}> = ({ orderSummary, isAccessoriesAvailable, advanceWizard, regressWizard }) => {
  const isAdmin = useIsAdmin()

  const shopCtx = useShopNewOrderContext()
  const [selectedCategoryIds, setSelectedCategoryIds] = shopCtx.selectedCategoryIds
  const [selectedProductIds] = shopCtx.selectedProductIds
  const [selectedPolicyId] = shopCtx.selectedPolicyId
  const [stockFilter, setStockFilter] = shopCtx.stockFilter
  const [sortParam, setSortParam] = shopCtx.sortParam
  const [sortOrder, setSortOrder] = shopCtx.sortOrder
  const searchFilter = shopCtx.searchFilter
  const [activeStepName, setActiveStepName] = shopCtx.activeStepName
  const [isWholeVendorsCatalogueVisible] = shopCtx.isWholeVendorsCatalogueVisible
  const [costObjectId] = shopCtx.costObjectId
  const [orderUserType] = shopCtx.orderUserType

  const setSearchFilter = shopCtx.setSearchFilter
  const [everSelectedCategoryIds] = useEverSelectedCategories(selectedCategoryIds)

  const { data: policy, isLoading: isPolicyDetailsLoading } = useQuery(
    selectedPolicyId ? queries.policyDetails(selectedPolicyId) : [''],
    () => {
      if (!selectedPolicyId) throw Error('No policy selected!')
      return ecomPolicyResources.getPolicyDetails(selectedPolicyId)
    }
  )
  const { data: shopSetup } = useQuery(commonQueries.employees(), () =>
    employeeResources.getList(isAdmin)
  )

  function getPriceListQuery() {
    if (!selectedPolicyId) return ['']
    const customerId = getCostObjectCustomerId({
      costObjectId,
      orderUserType,
      data: shopSetup,
    })
    if (isWholeVendorsCatalogueVisible)
      return queries.policyProductsFromWholeVendorCatalogue(selectedPolicyId, customerId)
    return queries.policyPriceList(selectedPolicyId, customerId)
  }

  const priceListQuery = useQuery(
    getPriceListQuery(),
    () => {
      if (!selectedPolicyId) throw Error('No policy selected!')
      const customerId = getCostObjectCustomerId({
        costObjectId,
        orderUserType,
        data: shopSetup,
      })
      if (isWholeVendorsCatalogueVisible)
        return ecomPolicyResources.getPolicyPriceListForWholeVendorCatalogue(
          selectedPolicyId,
          customerId
        )
      return ecomPolicyResources.getPolicyPriceList(selectedPolicyId, customerId)
    },
    { enabled: Boolean(selectedPolicyId) }
  )
  const policyListQuery = useQuery(
    costObjectId ? queries.costObjectPolicyList(costObjectId) : [''],
    () => {
      if (!costObjectId) throw Error('Cost object id missing!')
      return ecomPolicyResources.getCostObjectPolicyList(costObjectId)
    },
    { enabled: Boolean(costObjectId) }
  )

  const selectedPolicy = policyListQuery.data?.find(({ id }) => id === selectedPolicyId)
  const isRelationsIgnored = Boolean(selectedPolicy?.isRelationsIgnored)

  const list = useMemo(
    () =>
      getGroupedDataList({
        priceList: priceListQuery.data?.response,
        groupNames: priceListQuery.data?.groupNames,
        stockFilter,
        searchQuery: searchFilter,
        selectedCategoryIds,
        selectedProductIds,
        activeStepName,
        everSelectedCategoryIds,
        isRelationsIgnored,
      }),
    [
      activeStepName,
      everSelectedCategoryIds,
      isRelationsIgnored,
      priceListQuery.data?.groupNames,
      priceListQuery.data?.response,
      searchFilter,
      selectedCategoryIds,
      selectedProductIds,
      stockFilter,
    ]
  )

  useEffect(() => {
    setActiveStepName('PRODUCTS')
  }, [])

  useEffect(
    // Reset filters when leaving this step
    () => () => {
      setStockFilter(false)
      setSortParam(undefined)
      setSortOrder('asc')
      setSelectedCategoryIds(null)
      setSearchFilter('')
    },
    [setSearchFilter, setSelectedCategoryIds, setSortOrder, setSortParam, setStockFilter]
  )

  const sortedInventoryList = orderBy(list.groupedAndFiltered, sortParam, sortOrder)
  const { visibleList, ListLoaderComponent } = useRenderLimit(
    Object.values(sortedInventoryList) || [],
    24
  )

  return (
    <div>
      <Filter availableCategories={list.availableCategories || []} />

      {priceListQuery.isLoading || isPolicyDetailsLoading ? (
        <ProductsSkeleton />
      ) : visibleList.length === 0 ? (
        <NoResults>
          <Translation id="No products..." />
        </NoResults>
      ) : (
        <>
          {policy?.stepMainDescription && (
            <InfoBox emphasis="warning" marginBottom={4} marginTop={4}>
              <h3 className="m-0 whitespace-pre-wrap text-sm font-normal">
                {policy.stepMainDescription}
              </h3>
            </InfoBox>
          )}

          <div className="inventory__products">
            {visibleList.map(group => (
              <ProductGroup key={group.groupKey} {...group} />
            ))}
          </div>
        </>
      )}

      <ListLoaderComponent />

      <ProgressButtons
        orderSummary={orderSummary}
        isAccessoriesAvailable={isAccessoriesAvailable}
        advanceWizard={advanceWizard}
        regressWizard={regressWizard}
      />
    </div>
  )
}

export default StepProducts

const ProductsSkeleton: FC = () => {
  const tenElements = new Array(10)
  tenElements.fill(null)

  return (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(13rem,1fr))]">
      {tenElements.map((_, index) => (
        <div
          key={index}
          className="pointer-events-none m-1 rounded-md border border-transparent bg-transparent p-0"
        >
          <Skeleton height="216px" />
        </div>
      ))}
    </div>
  )
}
